/**
 * VideoCompositionPlayer
 *
 * React wrapper for the video-composition.html iframe.
 * Provides seamless multi-slide video playback with transitions.
 *
 * Features:
 * - Hosts video-composition.html in an iframe
 * - Communicates via postMessage (useVideoComposition hook)
 * - Exposes controls via imperative handle
 * - Reports state changes to parent
 */

import {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useVideoComposition, type CompositionEvent, type BackgroundMusicConfig, type LoadManifestOptions } from '@/hooks/useVideoComposition';
import { GSAP_CACHE_VERSION } from '@/config/gsapViewer';

// Re-export for convenience
export type { BackgroundMusicConfig, LoadManifestOptions };
import type { VideoManifest, VideoPlaybackState } from '@/types/videoManifest';
import { cn } from '@/lib/utils';

export interface VideoCompositionPlayerRef {
  /** Start playback */
  play: () => void;
  /** Pause playback */
  pause: () => void;
  /** Toggle play/pause */
  togglePlay: () => void;
  /** Seek to a specific time */
  seek: (time: number, options?: { pauseAfterSeek?: boolean }) => void;
  /** Live scrub during user drag — cheap, no slide fetches; pair with scrubEnd on release */
  scrub: (time: number) => void;
  /** Commit a scrub: load target slide if needed, sync audio, auto-resume if was playing */
  scrubEnd: () => void;
  /** Go to a specific slide */
  goToSlide: (slideId: string) => void;
  /** Go to a specific slide and show its end frame (for preview) */
  goToSlidePreview: (slideId: string) => void;
  /** Navigate to a slide with animated transition (for slides presentation mode) */
  navigateSlideWithTransition: (slideId: string, direction: 'next' | 'prev') => void;
  /** Set playback rate */
  setPlaybackRate: (rate: number) => void;
  /** Invalidate a slide (after editing) */
  invalidateSlide: (slideId: string) => void;
  /** Reload the manifest with optional initial slide */
  reloadManifest: (manifest: VideoManifest, options?: LoadManifestOptions) => void;
  /** Get current state */
  getState: () => VideoPlaybackState;
  /** Set background music (video-level) */
  setBackgroundMusic: (config: BackgroundMusicConfig) => void;
  /** Update background music volume */
  setBackgroundMusicVolume: (volume: number) => void;
  /** Remove background music */
  removeBackgroundMusic: () => void;
  /** Set narrator volume for all slides (0-1) */
  setNarratorVolume: (volume: number) => void;
}

export interface VideoCompositionPlayerProps {
  /** Video manifest to play */
  manifest: VideoManifest | null;
  /** Conversation ID for API calls */
  conversationId: string;
  /** Initial slide ID to load (skips loading slide 0 if different) */
  initialSlideId?: string;
  /** Callback when playback state changes */
  onStateChange?: (state: VideoPlaybackState) => void;
  /** Callback when manifest is updated (durations corrected) */
  onManifestUpdate?: (manifest: VideoManifest) => void;
  /** Callback when a slide starts playing */
  onSlideStart?: (slideId: string) => void;
  /** Callback when video ends */
  onVideoEnd?: () => void;
  /** Callback when an error occurs */
  onError?: (message: string) => void;
  /** The player is about to move into this scene (fires again each frame while it cannot load it). */
  onTransitionStarted?: (toSlideId: string) => void;
  /** The player has (re)loaded its manifest. */
  onManifestLoaded?: () => void;
  /** The player has fetched this scene again in place (after invalidateSlide on the scene it is on). */
  onSlideReloaded?: (slideId: string) => void;
  /** Whether to show debug info */
  debug?: boolean;
  /** Callback when user activity is detected inside the iframe (mouse/touch) */
  onUserActivity?: (activityType?: string) => void;
  /** Explicit content auth token (public share/embed pass the session token here) */
  authToken?: string;
  /** Additional class names */
  className?: string;
}

export const VideoCompositionPlayer = forwardRef<
  VideoCompositionPlayerRef,
  VideoCompositionPlayerProps
>(function VideoCompositionPlayer(
  {
    manifest,
    conversationId: _conversationId, // Reserved for future API calls
    initialSlideId,
    onStateChange,
    onManifestUpdate,
    onSlideStart,
    onVideoEnd,
    onError,
    onTransitionStarted,
    onManifestLoaded,
    onSlideReloaded,
    onUserActivity,
    authToken,
    debug = false,
    className,
  },
  ref
) {
  // Event handler for composition events
  const handleEvent = useCallback((event: CompositionEvent) => {
    switch (event.type) {
      case 'slideStarted':
        if (event.slideId) {
          onSlideStart?.(event.slideId);
        }
        break;
      case 'videoEnded':
        onVideoEnd?.();
        break;
      case 'error':
        if (event.message) {
          onError?.(event.message);
        }
        break;
      case 'transitionStarted':
        if (event.toSlide) onTransitionStarted?.(event.toSlide);
        break;
      case 'manifestLoaded':
        onManifestLoaded?.();
        break;
      case 'slideReloaded':
        if (event.slideId) onSlideReloaded?.(event.slideId);
        break;
    }
  }, [onSlideStart, onVideoEnd, onError, onTransitionStarted, onManifestLoaded, onSlideReloaded]);

  // Use the composition hook
  const {
    iframeRef,
    state,
    isReady,
    loadManifest,
    play,
    pause,
    seek,
    seekDisplay,
    scrub,
    scrubEnd,
    goToSlide,
    goToSlidePreview,
    navigateSlideWithTransition,
    setPlaybackRate,
    invalidateSlide,
    setBackgroundMusic,
    setBackgroundMusicVolume,
    removeBackgroundMusic,
    setNarratorVolume,
  } = useVideoComposition({
    onStateChange,
    onManifestUpdate,
    onEvent: handleEvent,
    onUserActivity,
    authToken,
  });

  // Build the iframe URL with cache-busting version param
  const iframeSrc = useMemo(() => {
    const baseUrl = '/gsap_framework/video-composition.html';
    const params = new URLSearchParams();
    params.set('v', GSAP_CACHE_VERSION);

    if (debug) {
      params.set('debug', 'true');
    }

    return `${baseUrl}?${params.toString()}`;
  }, [debug]);

  // Track which manifest we've loaded to avoid infinite reload loops
  // We use a signature based on slide IDs and count, NOT durations
  // This way duration corrections from the iframe don't trigger reloads
  const loadedManifestSignatureRef = useRef<string | null>(null);

  // Compute manifest signature - based on structure and transitions, not durations
  const getManifestSignature = useCallback((m: VideoManifest): string => {
    // Include slide IDs, jsonPaths (without tokens), and transitions to detect real changes
    const slideSignatures = m.slides.map(s => {
      // Strip token from jsonPath for comparison
      const pathWithoutToken = s.jsonPath?.split('?')[0] || s.id;
      // Include transition type and duration in signature
      const transitionSig = s.transitionOut
        ? `${s.transitionOut.type}:${s.transitionOut.duration}`
        : 'none';
      return `${s.id}:${pathWithoutToken}:${transitionSig}`;
    });
    // Also include default transition in signature
    const defaultTransitionSig = m.defaultTransition
      ? `${m.defaultTransition.type}:${m.defaultTransition.duration}`
      : 'default';
    // A program edit (set_video_program) changes nothing above but must reload the player
    let programSig = 'noprog';
    if (m.program) {
      const txt = JSON.stringify(m.program);
      let h = 5381;
      for (let i = 0; i < txt.length; i++) h = ((h << 5) + h + txt.charCodeAt(i)) | 0;
      programSig = `prog${txt.length}.${h >>> 0}`;
    }
    return `${m.slides.length}:${defaultTransitionSig}:${programSig}:${slideSignatures.join(',')}`;
  }, []);

  // Load manifest when it changes structurally (not just duration updates)
  useEffect(() => {
    if (isReady && manifest) {
      const currentSignature = getManifestSignature(manifest);

      if (loadedManifestSignatureRef.current !== currentSignature) {
        console.log('[VideoCompositionPlayer] Loading manifest (new structure):', {
          slideCount: manifest.slides.length,
          previousSignature: loadedManifestSignatureRef.current?.substring(0, 50),
          newSignature: currentSignature.substring(0, 50),
          initialSlideId: initialSlideId || 'none (will use slide 0)',
        });
        loadedManifestSignatureRef.current = currentSignature;
        // Pass initialSlideId to avoid loading slide 0 when we know we're going to a different slide
        loadManifest(manifest, { initialSlideId });
      } else {
        console.log('[VideoCompositionPlayer] Skipping manifest reload (same structure, likely duration update)');
      }
    }
  }, [isReady, manifest, loadManifest, getManifestSignature, initialSlideId]);

  // Toggle play/pause helper
  const togglePlay = useCallback(() => {
    if (state.isPlaying) {
      pause();
    } else {
      play();
    }
  }, [state.isPlaying, play, pause]);

  // Reload manifest with optional initial slide
  const reloadManifest = useCallback((newManifest: VideoManifest, options?: LoadManifestOptions) => {
    loadManifest(newManifest, options);
  }, [loadManifest]);

  // Get current state
  const getState = useCallback(() => state, [state]);

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    play,
    pause,
    togglePlay,
    seek,
    scrub,
    scrubEnd,
    goToSlide,
    goToSlidePreview,
    navigateSlideWithTransition,
    setPlaybackRate,
    invalidateSlide,
    reloadManifest,
    getState,
    setBackgroundMusic,
    setBackgroundMusicVolume,
    removeBackgroundMusic,
    setNarratorVolume,
  }), [play, pause, togglePlay, seek, seekDisplay, scrub, scrubEnd, goToSlide, goToSlidePreview, navigateSlideWithTransition, setPlaybackRate, invalidateSlide, reloadManifest, getState, setBackgroundMusic, setBackgroundMusicVolume, removeBackgroundMusic, setNarratorVolume]);

  return (
    <div className={cn('relative w-full overflow-hidden', className)}>
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        className="absolute inset-0 w-full h-full border-0"
        style={{ background: 'transparent' }}
        allow="autoplay; fullscreen"
        title="Video Composition Player"
      />

      {/* Loading indicator when not ready */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-2 border-muted-foreground/20 border-t-muted-foreground rounded-full animate-spin" />
            <span className="text-muted-foreground text-sm">Loading composition...</span>
          </div>
        </div>
      )}
    </div>
  );
});
